import React, { useState } from "react";
import { IDSButton, IDSNavigationLocal, IDSBadge } from "@inera/ids-react";

export const sectionAnchorId = (sectionId) => `formsection-${sectionId}`;

/**
 * Sticky navigator listing the top-level sections of a form with a
 * completeness badge per section. Mirrors the "Avsnittsnavigering" used by
 * the test client's QuestionSection.
 *
 * @param {Array<{sectionId, label, missing, complete}>} sectionStatus
 */
function SectionNavigator({ sectionStatus = [] }) {
  const [navCollapsed, setNavCollapsed] = useState(false);

  if (sectionStatus.length <= 1) return null;

  const scrollToSection = (sectionId) => {
    const el =
      typeof document !== "undefined"
        ? document.getElementById(sectionAnchorId(sectionId))
        : null;
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <aside
      aria-label="Avsnittsnavigering"
      style={{
        position: "sticky",
        top: "1rem",
        order: -1,
        flex: navCollapsed ? "0 0 auto" : "0 0 18rem",
        minWidth: 0,
        alignSelf: "flex-start",
        maxHeight: "calc(100vh - 2rem)",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {navCollapsed ? (
        <IDSButton
          size="s"
          secondary
          icon="chevron-right"
          onClick={() => setNavCollapsed(false)}
          aria-label="Visa avsnittsnavigering"
          aria-expanded={false}
        >
          Avsnitt
        </IDSButton>
      ) : (
        <div style={{ position: "relative" }}>
          <IDSButton
            size="s"
            secondary
            iconButton
            icon="chevron-down"
            onClick={() => setNavCollapsed(true)}
            aria-label="Dölj avsnittsnavigering"
            aria-expanded={true}
            style={{ position: "absolute", top: "0.5rem", right: 0, zIndex: 1 }}
          />
          <IDSNavigationLocal headline="Avsnitt">
            {sectionStatus.map((s) => (
              <button
                key={s.sectionId}
                type="button"
                className="ids-navigation-local__link"
                onClick={() => scrollToSection(s.sectionId)}
                aria-label={
                  typeof s.missing === "number"
                    ? s.complete
                      ? `${s.label}: alla obligatoriska frågor besvarade`
                      : `${s.label}: ${s.missing} obesvarade obligatoriska frågor`
                    : s.commentCount > 0
                    ? `${s.label}: ${s.commentCount} kommentarer`
                    : s.label
                }
                style={{
                  width: "100%",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  fontSize: "0.95rem",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  {typeof s.missing === "number" ? (
                    s.complete ? (
                      <IDSBadge type="success" icon="success" />
                    ) : (
                      <IDSBadge type="error">{s.missing}</IDSBadge>
                    )
                  ) : (
                    s.commentCount > 0 && <IDSBadge type="attention">{s.commentCount}</IDSBadge>
                  )}
                  <span style={{ flex: 1, overflowWrap: "anywhere" }}>{s.label}</span>
                </span>
              </button>
            ))}
          </IDSNavigationLocal>
        </div>
      )}
    </aside>
  );
}

export default SectionNavigator;
